import type { OverviewView } from "@slfinrtl/ui-bff-routes"
import { getVehicleDetailsOverviewRoute } from "@slfinrtl/ui-bff-routes"
import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { StandardEquipmentContainer } from "src/containers/standardEquipment/StandardEquipmentContainer"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { useBff } from "src/hooks"
import type { VehicleDetailsStage } from "../common/VehicleDetailsStage"
import type { VehicleDetailsStageLinkState } from "../common/VehicleDetailsStageLink"
import { OverviewCampaigns } from "../components/campaigns/OverviewCampaigns"
import { useVehicleDetailsContext } from "../context/VehicleDetailsContext"
import { CustomerFeedbackContainer } from "./customerFeedbackContainer/CustomerFeedbackContainer"
import { FinancingContainer } from "./financingContainer/FinancingContainer"
import { InventoryInformationContainer } from "./inventoryInformationContainer/InventoryInformationContainer"
import { ListingDescriptionContainer } from "./listingDescriptionContainer/ListingDescriptionContainer"
import { ListingScoreHintsContainer } from "./listingScoreHintsContainer/ListingScoreHintsContainer"
import { ModelInformationContainer } from "./modelInformationContainer/ModelInformationContainer"
import { NotesContainer } from "./notesContainer/NotesContainer"
import { PorscheApprovedVehicleEditContainer } from "./porscheApprovedPublicOfferContainer/PorscheApprovedVehicleEditContainer"
import { SpecialEquipmentContainer } from "./specialEquipmentContainer/SpecialEquipmentStageContainer"
import { OverviewSummaryContainer } from "./summaryContainer/OverviewSummaryContainer"
import { TechnicalDataContainer } from "./technicalDataContainer/TechnicalDataContainer"
import { VisibilityContainer } from "./visibilityContainer/VisibilityContainer"

export const OverviewContainer = withErrorHandler(() => {
	const [view, setView] = useState<OverviewView | null>()

	const { inventoryId } = useVehicleDetailsContext()
	const navigate = useNavigate()

	const fetchView = useBff(
		getVehicleDetailsOverviewRoute,
		{
			body: undefined,
			pathParams: { inventoryId },
			queryParams: {},
		},
		"inline",
	)
	useEffect(() => {
		fetchView()
			.then(setView)
			.catch(() => setView(null))
	}, [fetchView, setView])

	const handleEditStageClick = useCallback(
		(editStage: VehicleDetailsStage) => {
			navigate(
				{},
				{
					preventScrollReset: true,
					replace: true,
					state: { editStage, scrollInstantly: true } satisfies VehicleDetailsStageLinkState,
				},
			)
		},
		[navigate],
	)

	return (
		<>
			<ErrorNotificationInline dismissButton={false} />
			{view !== null && (
				<OverviewSummaryContainer
					view={view?.summary}
					imageEdit={view?.imageEdit}
					isFinancingEnabled={view?.financing.enabled ?? false}
					onEditStageClick={handleEditStageClick}
				/>
			)}
			<ListingScoreHintsContainer imageEditView={view?.imageEdit} onEditStageClick={handleEditStageClick} />
			<NotesContainer />
			{view?.campaigns && <OverviewCampaigns view={view.campaigns} />}
			<SpecialEquipmentContainer />
			<StandardEquipmentContainer inventoryId={inventoryId} />
			<InventoryInformationContainer />
			<ListingDescriptionContainer />
			<VisibilityContainer />
			{view?.porscheApproved && (
				<PorscheApprovedVehicleEditContainer stageEditAction={view.porscheApproved.editAction} />
			)}
			{view?.financing.enabled && <FinancingContainer />}
			<ModelInformationContainer />
			<TechnicalDataContainer />
			<CustomerFeedbackContainer />
		</>
	)
})
